/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CFormLabel,
  CRow,
  CSpinner,
  CAlert,
  CBadge
} from '@coreui/react';
import { useTranslation } from '../../../hooks/useTranslation';
import { getReportingWeaveDropdown, getWeave, getWeaveFactors } from '../../../services/endpoints';
import { formatDateOnly } from '../../../helpers/dateHelper';
import './CaglikReport.scss';
import SearchableSelect from '../../../components/SearchableSelect/SearchableSelect';

function CaglikReport({ initialWeaveId = '', embeddedPreview = false }) {
  const { t } = useTranslation();

  const [loadingDropdown, setLoadingDropdown] = useState(false);
  const [dropdownItems, setDropdownItems] = useState([]);
  const [selectedWeaveId, setSelectedWeaveId] = useState('');

  const [loadingReport, setLoadingReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [weaveFactorRowsByThreadType, setWeaveFactorRowsByThreadType] = useState({ 1: [], 2: [] });
  const [error, setError] = useState('');

  const normalizeWeaveFactorsByThreadType = (items = []) => {
    const grouped = { 1: [], 2: [] };

    if (!Array.isArray(items)) return grouped;

    items.forEach((item) => {
      const threadType = Number(item?.ThreadType);
      const start = Number(item?.FirstColon);
      const end = Number(item?.SecondColon);
      const factor = Number(item?.Factor);

      if (![1, 2].includes(threadType)) return;
      if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(factor)) return;
      if (start <= 0 || end < start || factor <= 0) return;

      grouped[threadType].push({ start, end, factor });
    });

    grouped[1].sort((a, b) => a.start - b.start);
    grouped[2].sort((a, b) => a.start - b.start);

    return grouped;
  };

  const fetchDropdownByWeaveCode = async (searchTerm) => {
    const trimmedSearchTerm = (searchTerm || '').trim();

    if (!trimmedSearchTerm) {
      setDropdownItems([]);
      return;
    }

    try {
      setLoadingDropdown(true);
      setError('');
      const res = await getReportingWeaveDropdown({ WeaveCode: trimmedSearchTerm });
      const data = res?.data || res;
      const items = data?.WeavesDropdown || [];
      setDropdownItems(items);
    } catch (e) {
      console.error('CaglikReport dropdown error:', e);
      setError(t('errorLoadingDropdown') || 'Weave listesi yüklenirken hata oluştu');
      setDropdownItems([]);
    } finally {
      setLoadingDropdown(false);
    }
  };

  const fetchReportByWeaveId = async (weaveId) => {
    const normalizedWeaveId = String(weaveId ?? '').trim();
    if (!normalizedWeaveId) return;

    try {
      setLoadingReport(true);
      setError('');
      const [weaveResult, factorResult] = await Promise.allSettled([
        getWeave({ WeaveId: normalizedWeaveId }),
        getWeaveFactors({ WeaveId: normalizedWeaveId })
      ]);

      if (weaveResult.status === 'rejected') {
        throw weaveResult.reason;
      }

      const res = weaveResult.value;
      const data = res?.data || res;
      const weaveList = data?.WeaveList || [];

      if (factorResult.status === 'fulfilled') {
        const factorRes = factorResult.value;
        const factorData = factorRes?.data || factorRes;
        setWeaveFactorRowsByThreadType(
          normalizeWeaveFactorsByThreadType(factorData?.WeaveFactorList || [])
        );
      } else {
        console.error('CaglikReport weave factor fetch error:', factorResult.reason);
        setWeaveFactorRowsByThreadType({ 1: [], 2: [] });
      }

      if (weaveList.length > 0) {
        setReportData(weaveList[0]);
      } else {
        setError(t('noDataFound') || 'Seçilen weave için veri bulunamadı');
        setReportData(null);
        setWeaveFactorRowsByThreadType({ 1: [], 2: [] });
      }
    } catch (e) {
      console.error('CaglikReport fetch error:', e);
      setError(t('errorLoadingReport') || 'Rapor yüklenirken hata oluştu');
      setReportData(null);
      setWeaveFactorRowsByThreadType({ 1: [], 2: [] });
    } finally {
      setLoadingReport(false);
    }
  };

  const handleGetReport = async () => {
    if (!selectedWeaveId) return;
    await fetchReportByWeaveId(selectedWeaveId);
  };

  useEffect(() => {
    const normalizedInitialWeaveId = String(initialWeaveId ?? '').trim();
    if (!normalizedInitialWeaveId) return;

    setSelectedWeaveId(normalizedInitialWeaveId);
    fetchReportByWeaveId(normalizedInitialWeaveId);
  }, [initialWeaveId]);

  const dropdownOptions = useMemo(() => {
    return (dropdownItems || []).map(item => ({
      value: item.WeaveId,
      text: item.WeaveCode,
    }));
  }, [dropdownItems]);

  const fmtDate = (d) => {
    if (!d) return '';
    return formatDateOnly(d);
  };

  const fmtNum = (n) => {
    if (n == null || n === '') return '';
    try { return Number(n).toLocaleString('tr-TR'); } catch (e) { return String(n); }
  };

  const fmtDensity = (value) => {
    if (value == null || value === '') return '';
    const parsed = Number(String(value).trim().replace(',', '.'));
    if (!Number.isFinite(parsed)) return String(value);

    const scaled = Math.trunc(Math.abs(parsed));
    const sign = parsed < 0 ? '-' : '';
    const whole = Math.trunc(scaled / 100);
    const decimal = scaled % 100;

    if (decimal === 0) return `${sign}${whole}`;

    return `${sign}${whole},${String(decimal).padStart(2, '0')}`;
  };

  const fmtWeight = (weight) => {
    if (weight == null || weight === '') return '';
    const num = Number(weight);
    const result = num / 10000;
    return result.toFixed(2);
  };

  // Process weave colors data for warp and weft reports
  const processWeaveColors = (weaveColors = []) => {
    const warpColors = [];
    const weftColors = [];
    const maxPosition = Math.max(...weaveColors.map(wc => wc.Position || 0), 0);

    // Initialize arrays for all positions
    for (let i = 1; i <= maxPosition; i++) {
      warpColors[i] = { position: i, colors: [] };
      weftColors[i] = { position: i, colors: [] };
    }

    // Group colors by position and thread type
    weaveColors.forEach(wc => {
      const position = wc.Position || 1;
      const threadType = wc.ThreadType || 0;
      const colorData = {
        yarnName: wc.YarnName || '',
        yarnCode: wc.YarnCode || '',
        colorName: wc.ColorName || '',
        colorCode: wc.ColorCode || '',
        rowSorting: wc.RowSorting || null,
        threadCount: wc.ThreadCount || 0,
        weight: threadType === 1 ? wc.WeftWeight : wc.WaftWeight // Warp için WeftWeight, Weft için WaftWeight
      };

      if (threadType === 1) { // Atkı (Weft)
        if (!weftColors[position]) weftColors[position] = { position, colors: [] };
        weftColors[position].colors.push(colorData);
      } else if (threadType === 2) { // Çözgü (Warp)
        if (!warpColors[position]) warpColors[position] = { position, colors: [] };
        warpColors[position].colors.push(colorData);
      }
    });

    return { warpColors, weftColors, maxPosition };
  };

  const buildTemplateHTML = () => {
    if (!reportData) return '';

    const logoUrl = `${window.location.origin}/logo.jpg`;
    const tarihStr = fmtDate(reportData.InsertDateTime);
    const weaveCode = reportData.WeaveCode || '';
    const weaveType = reportData.WeaveTypeName || '';
    const warpDensity = fmtDensity(reportData.WarpDensity);
    const weftDensity = fmtDensity(reportData.WeftDensity);
    const reedWidth = reportData.ReedWidth || '';
    const reedNo = reportData.ReedNo || '';
    const dgts = reportData.Dgts || '';
    const totalThreads = fmtNum(reportData.TotalThreads);
    const description = reportData.Description || '';
    const threadName = reportData.ThreadName || '';
    const weavePhoto = reportData.Photo || '';
    const fabricPhoto = reportData.PhotoWeave || '';

    // Derive yarn info from WeaveColors
    const warpYarns = new Set();
    const weftYarns = new Set();
    if (reportData.WeaveColors) {
      reportData.WeaveColors.forEach(wc => {
        const yarnInfo = `${wc.YarnCode || ''} ${wc.YarnName || ''}`.trim();
        if (wc.ThreadType === 2 && yarnInfo) warpYarns.add(yarnInfo);
        if (wc.ThreadType === 1 && yarnInfo) weftYarns.add(yarnInfo);
      });
    }
    const warpYarnInfo = [...warpYarns].join(', ');
    const weftYarnInfo = [...weftYarns].join(', ');

    const { warpColors, weftColors, maxPosition } = processWeaveColors(reportData.WeaveColors);

    return `<!DOCTYPE html><html><head><meta charset="utf-8" />
    <title>Çağlık Raporu</title>
    <style>
      ${templateCSS}
    </style>
    </head><body>
      ${renderTemplateMarkup({
      logoUrl,
      tarihStr,
      weaveCode,
      weaveType,
      warpDensity,
      weftDensity,
      reedWidth,
      reedNo,
      dgts,
      totalThreads,
      description,
      threadName,
      weavePhoto,
      fabricPhoto,
      warpYarnInfo,
      weftYarnInfo,
      warpColors,
      weftColors,
      maxPosition,
      weaveFactorRowsByThreadType
    })}
    </body></html>`;
  };

  const handlePrint = () => {
    const html = buildTemplateHTML();
    const w = window.open('', '_blank', 'width=1100,height=800');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      w.print();
      w.onafterprint = () => w.close();
    }, 500);
  };
  const handleExportExcel = () => {
    if (!reportData) return;

    const logoUrl = `${window.location.origin}/logo.jpg`;
    const tarihStr = fmtDate(reportData.InsertDateTime);
    const weaveCode = reportData.WeaveCode || '';
    const weaveType = reportData.WeaveTypeName || '';
    const warpDensity = fmtDensity(reportData.WarpDensity);
    const weftDensity = fmtDensity(reportData.WeftDensity);
    const reedWidth = reportData.ReedWidth || '';
    const reedNo = reportData.ReedNo || '';
    const dgts = reportData.Dgts || '';
    const totalThreads = fmtNum(reportData.TotalThreads);
    const description = reportData.Description || '';
    const threadName = reportData.ThreadName || '';
    const weavePhoto = reportData.Photo || '';
    const fabricPhoto = reportData.PhotoWeave || '';

    // Derive yarn info from WeaveColors
    const warpYarns = new Set();
    const weftYarns = new Set();
    if (reportData.WeaveColors) {
      reportData.WeaveColors.forEach(wc => {
        const yarnInfo = `${wc.YarnCode || ''} ${wc.YarnName || ''}`.trim();
        if (wc.ThreadType === 2 && yarnInfo) warpYarns.add(yarnInfo);
        if (wc.ThreadType === 1 && yarnInfo) weftYarns.add(yarnInfo);
      });
    }
    const warpYarnInfo = [...warpYarns].join(', ');
    const weftYarnInfo = [...weftYarns].join(', ');

    const { warpColors, weftColors, maxPosition } = processWeaveColors(reportData.WeaveColors);

    const ctx = {
      logoUrl,
      tarihStr,
      weaveCode,
      weaveType,
      warpDensity,
      weftDensity,
      reedWidth,
      reedNo,
      dgts,
      totalThreads,
      description,
      threadName,
      weavePhoto,
      fabricPhoto,
      warpYarnInfo,
      weftYarnInfo,
      warpColors,
      weftColors,
      maxPosition,
      weaveFactorRowsByThreadType
    };

    const html = buildExcelHTMLFromCtx(ctx);
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `caglik_raporu_${weaveCode || 'rapor'}_${new Date().toISOString().slice(0, 10)}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setSelectedWeaveId('');
    setReportData(null);
    setWeaveFactorRowsByThreadType({ 1: [], 2: [] });
    setError('');
  };

  return (
    <div className={`caglik-report${embeddedPreview ? ' caglik-report--embedded' : ''}`}>
      <CContainer fluid className={embeddedPreview ? 'px-1' : 'px-4'}>
        {!embeddedPreview && (
          <div className="page-header mb-4">
            <h2 className="page-title">
              <i className="fas fa-palette"></i>
              {t('caglikReport') || 'Çağlık Raporu'}
            </h2>
            <p className="page-subtitle text-muted">
              {t('caglikReportDescription') || 'Weave seçerek renk raporlarını görüntüleyebilir, yazdırabilir veya Excel formatında indirebilirsiniz.'}
            </p>
          </div>
        )}

        {error && (
          <CAlert
            color="danger"
            dismissible={!embeddedPreview}
            onClose={!embeddedPreview ? () => setError('') : undefined}
            className="mb-4"
          >
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </CAlert>
        )}

        {!embeddedPreview && (
          <CRow className="mb-4 justify-content-center">
            <CCol xs={12} xl={10} xxl={8}>
              <CCard className="shadow-sm border-0 selection-card">
                <CCardHeader className="gradient-header text-white">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-search me-2"></i>
                    <h5 className="mb-0">{t('weaveSelection') || 'Weave Seçimi'}</h5>
                  </div>
                </CCardHeader>
                <CCardBody className="p-4">
                  <CRow className="g-3">
                    <CCol xs={12} lg={6}>
                      <CFormLabel htmlFor="weaveSelect" className="form-label-required">
                        {t('selectWeave') || 'Tip Kodu Seçin'}
                      </CFormLabel>
                      <SearchableSelect
                        id="weaveSelect"
                        options={dropdownOptions}
                        value={selectedWeaveId}
                        onChange={(v) => setSelectedWeaveId(v)}
                        placeholder={loadingDropdown ? (t('loading') || 'Yükleniyor...') : 'Tip kodu ile ara...'}
                        displayKey="text"
                        valueKey="value"
                        disabled={loadingDropdown}
                        onSearchTermChange={fetchDropdownByWeaveCode}
                      />
                      {dropdownOptions.length > 0 && (
                        <small className="text-muted mt-1 d-block">
                          <i className="fas fa-info-circle me-1"></i>
                          {dropdownOptions.length} Tip kaydı mevcut
                        </small>
                      )}
                    </CCol>
                    <CCol xs={12} lg={6}>
                      <CFormLabel className="d-none d-lg-block" style={{ visibility: 'hidden' }}>
                        &nbsp;
                      </CFormLabel>
                      <div className="action-buttons-group">
                        <CButton
                          className="btn-report-generate"
                          onClick={handleGetReport}
                          disabled={!selectedWeaveId || loadingReport || loadingDropdown}
                          title={t('generateReport') || 'Raporu Getir'}
                        >
                          {loadingReport ? (
                            <>
                              <CSpinner size="sm" className="me-2" />
                              {t('loading') || 'Yükleniyor...'}
                            </>
                          ) : (
                            <>
                              <i className="fas fa-file-alt me-2"></i>
                              {t('generateReport') || 'Raporu Getir'}
                            </>
                          )}
                        </CButton>
                        {(selectedWeaveId || reportData) && (
                          <CButton
                            className="btn-clear"
                            onClick={resetForm}
                            disabled={loadingReport || loadingDropdown}
                            title={t('clear') || 'Temizle'}
                          >
                            <span className="clear-icon">×</span>
                            <span className="btn-text">{t('clear') || 'Temizle'}</span>
                          </CButton>
                        )}
                      </div>
                    </CCol>
                  </CRow>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        )}

        {embeddedPreview && loadingReport && !reportData && (
          <div className="py-4 text-center text-muted">
            <CSpinner size="sm" className="me-2" />
            {t('loading') || 'Yükleniyor...'}
          </div>
        )}

        {reportData && (
          <>
            {!embeddedPreview && (
              <CRow className="mb-4 justify-content-center">
                <CCol xs={12} xl={10} xxl={8}>
                  <CCard className="shadow-sm border-0 details-card">
                    <CCardHeader className="details-header">
                      <div className="details-header-content">
                        <div className="details-info">
                          <h5 className="mb-2 details-title">
                            <i className="fas fa-palette me-2"></i>
                            {t('reportDetails') || 'Rapor Detayları'}
                          </h5>
                          <div className="details-badges">
                            <CBadge className="detail-badge code-badge">
                              <i className="fas fa-barcode me-1"></i>
                              {reportData.WeaveCode}
                            </CBadge>
                            {reportData.WeaveTypeName && (
                              <CBadge className="detail-badge type-badge">
                                <i className="fas fa-layer-group me-1"></i>
                                {reportData.WeaveTypeName}
                              </CBadge>
                            )}
                            {reportData.InsertDateTime && (
                              <CBadge className="detail-badge date-badge">
                                <i className="fas fa-calendar me-1"></i>
                                {fmtDate(reportData.InsertDateTime)}
                              </CBadge>
                            )}
                          </div>
                        </div>
                        <div className="action-buttons-export">
                          <CButton
                            className="btn-action-print"
                            size="sm"
                            onClick={handlePrint}
                            disabled={!reportData}
                          >
                            <i className="fas fa-print me-1"></i>
                            <span className="btn-text">{t('print') || 'Yazdır'}</span>
                          </CButton>
                          <CButton
                            className="btn-action-excel"
                            size="sm"
                            onClick={handleExportExcel}
                            disabled={!reportData}
                          >
                            <i className="fas fa-file-excel me-1"></i>
                            <span className="btn-text">Excel</span>
                          </CButton>
                        </div>
                      </div>
                    </CCardHeader>
                  </CCard>
                </CCol>
              </CRow>
            )}

            <CRow>
              <CCol xs={12}>
                <CCard className={`report-preview-card ${embeddedPreview ? 'border-0 shadow-sm' : 'shadow'}`}>
                  <CCardBody className="p-0">
                    <div className="report-a4-preview">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            const warpYarnsSet = new Set();
                            const weftYarnsSet = new Set();
                            (reportData?.WeaveColors || []).forEach(wc => {
                              const yi = `${wc.YarnCode || ''} ${wc.YarnName || ''}`.trim();
                              if (wc.ThreadType === 2 && yi) warpYarnsSet.add(yi);
                              if (wc.ThreadType === 1 && yi) weftYarnsSet.add(yi);
                            });
                            return renderTemplateMarkup({
                              logoUrl: `${window.location.origin}/logo.jpg`,
                              tarihStr: fmtDate(reportData?.InsertDateTime),
                              weaveCode: reportData?.WeaveCode || '',
                              weaveType: reportData?.WeaveTypeName || '',
                              warpDensity: fmtDensity(reportData?.WarpDensity),
                              weftDensity: fmtDensity(reportData?.WeftDensity),
                              reedWidth: reportData?.ReedWidth || '',
                              reedNo: reportData?.ReedNo || '',
                              dgts: reportData?.Dgts || '',
                              totalThreads: fmtNum(reportData?.TotalThreads),
                              description: reportData?.Description || '',
                              threadName: reportData?.ThreadName || '',
                              weavePhoto: reportData?.Photo || '',
                              fabricPhoto: reportData?.PhotoWeave || '',
                              warpYarnInfo: [...warpYarnsSet].join(', '),
                              weftYarnInfo: [...weftYarnsSet].join(', '),
                              ...processWeaveColors(reportData?.WeaveColors || []),
                              weaveFactorRowsByThreadType
                            });
                          })()
                        }} />
                    </div>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </>
        )}
      </CContainer>
    </div>
  );
}

export default CaglikReport;

// Enhanced CSS for better visual design - Professional look matching the image
const templateCSS = `
  @page { 
    size: A4 landscape; 
    margin: 10mm; 
  }
  @media print {
    @page { 
      size: landscape; 
      margin: 10mm; 
    }
    html, body {
      width: auto;
      height: auto;
    }
  }
  body { 
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
    color: #333; 
    line-height: 1.4;
    margin: 0;
    padding: 0;
    font-size: 16px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .report-wrap { 
    width: 100%; 
    background: #ffffff;
  }
  .page-meta-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  .page-meta-left {
    flex: 1;
    display: flex;
    align-items: center;
  }
  .page-meta-spacer {
    width: 1px;
    height: 1px;
  }
  .tip-code-box {
    display: inline-flex;
    align-items: center;
    border: 2px solid #000;
    background: #fff;
    font-size: 13px;
    line-height: 1;
    min-height: 34px;
  }
  .tip-code-box .tip-label {
    font-weight: 700;
    background: #f0f0f0;
    border-right: 2px solid #000;
    padding: 8px 10px;
    white-space: nowrap;
  }
  .tip-code-box .tip-value {
    font-weight: 600;
    padding: 8px 12px;
    min-width: 120px;
  }
  .page-number-box {
    border: 2px solid #000;
    background: #fff;
    font-size: 13px;
    font-weight: 700;
    padding: 7px 12px;
    line-height: 1;
    white-space: nowrap;
  }
  .report-page-break {
    page-break-before: always;
    margin-top: 60px;
  }
  .report-gap {
    height: 60px;
  }
  .header-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    margin-bottom: 8px;
    table-layout: fixed;
    border: 2px solid #000;
  }
  .header-table td {
    border: 2px solid #000;
    padding: 6px 8px;
    vertical-align: middle;
  }
  .header-table .label-cell {
    font-weight: 700;
    background: #f0f0f0;
    white-space: nowrap;
    color: #000;
    width: 145px;
  }
  .header-table .value-cell {
    background: #ffffff;
    color: #000;
    width: 120px;
  }
  .header-table .description-area {
    text-align: left !important;
    vertical-align: top !important;
    background: #ffffff;
    padding: 4px 8px 0 8px;
  }
  .header-table .description-area .description-title {
    font-size: 12px;
    font-weight: 800;
    color: #000;
    margin: 0;
    line-height: 1.1;
    text-align: left;
  }
  .header-table .description-area .description-text {
    font-size: 12px;
    line-height: 1.4;
    color: #222;
    margin-top: 4px;
    text-align: left;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  .header-table .brand-area {
    text-align: center;
    vertical-align: middle;
    background: #ffffff;
    padding: 8px;
  }
  .header-table .brand-area img {
    max-height: 36px;
    max-width: 90px;
    object-fit: contain;
  }
  .tip-header-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    margin-bottom: 8px;
    table-layout: fixed;
    border: 2px solid #000;
  }
  .tip-header-table td {
    border: 2px solid #000;
    padding: 6px 8px;
    vertical-align: middle;
  }
  .tip-header-table .label-cell {
    font-weight: 700;
    background: #f0f0f0;
    white-space: nowrap;
    color: #000;
    width: 145px;
  }
  .tip-header-table .value-cell {
    background: #ffffff;
    color: #000;
    width: 120px;
  }
  .company-name {
    font-size: 12px;
    font-weight: 600;
    margin-top: 4px;
    color: #000;
  }
  .report-main-title {
    font-size: 14px;
    font-weight: 700;
    margin-top: 8px;
    color: #000;
    width: 100%;
    text-align: center;
  }
  .color-report-section {
    margin: 8px 0;
  }
  .color-report-title {
    font-size: 15px;
    font-weight: 700;
    color: #000;
    padding: 6px 10px;
    border: 2px solid #000;
    border-bottom: none;
    background: #f0f0f0;
  }
  .color-report-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    table-layout: fixed;
    border: 2px solid #000;
  }
  .color-report-table th,
  .color-report-table td {
    border: 1px solid #000;
    padding: 2px 3px;
    text-align: center;
    background: #ffffff;
    white-space: nowrap;
    line-height: 2.0;
  }
  .color-report-table th {
    background: #f0f0f0;
    font-weight: 600;
    color: #000;
  }
  .color-report-table .yarn-name {
    text-align: left;
    font-weight: 500;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    width: 200px;
    max-width: 200px;
    font-size: 12px;
    vertical-align: top;
  }
  .color-report-table .yarn-code {
    text-align: left;
    white-space: nowrap;
    width: 80px;
    max-width: 80px;
    font-size: 12px;
  }
  .color-report-table .color-name {
    text-align: left;
    font-weight: 500;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    width: 80px;
    max-width: 80px;
    font-size: 12px;
    vertical-align: top;
  }
  .color-report-table .color-code {
    text-align: left;
    white-space: nowrap;
    width: 60px;
    max-width: 60px;
    font-size: 12px;
  }
  .color-report-table .total-col {
    background: #e9ecef;
    font-weight: 600;
    width: 50px;
    max-width: 50px;
  }
  .color-report-table .percentage-col {
    background: #e9ecef;
    font-weight: 600;
    width: 55px;
    max-width: 55px;
  }
  .color-report-table .weight-col {
    background: #e9ecef;
    width: 70px;
    max-width: 70px;
    font-size: 12px;
  }
  .color-report-table .position-col {
    width: 20px;
    min-width: 20px;
  }
  .yarn-label-row td {
    border: 1px solid #000;
    text-align: right;
    font-weight: 600;
    font-size: 12px;
    padding: 1px 3px;
  }
  .factor-row td {
    border-left: none !important;
    border-right: none !important;
  }
  .factor-row .position-col {
    position: relative;
    overflow: visible;
  }
  .photo-page-section {
    border: none;
    padding: 0;
    background: #fff;
    min-height: 680px;
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    gap: 10px;
  }
  .photo-page-left {
    width: 28%;
    max-width: 28%;
    min-height: 680px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .photo-page-right {
    width: 72%;
    max-width: 72%;
    min-height: 680px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
  }
  .photo-page-frame {
    border: none;
    min-height: 680px;
    width: 100%;
    max-width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: #fff;
  }
  .report-photo-large {
    width: 100%;
    max-width: 100%;
    max-height: 680px;
    object-fit: contain;
    display: block;
  }
`;

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const sanitizeImageSrc = (value) => {
  const src = String(value ?? '').trim();
  if (!src) return '';

  if (src.startsWith('data:image/')) return src;
  if (src.startsWith('blob:')) return src;
  if (src.startsWith('/')) return src;

  try {
    const parsed = new URL(src, window.location.origin);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.toString();
    }
  } catch (e) {
    return '';
  }

  return '';
};

// Returns inner markup (without html/head/body) to enable reuse in React preview
function renderTemplateMarkup(params) {
  const {
    logoUrl,
    tarihStr,
    weaveCode,
    weaveType,
    warpDensity,
    weftDensity,
    reedWidth,
    reedNo,
    dgts,
    totalThreads,
    description,
    threadName,
    weavePhoto,
    fabricPhoto,
    warpYarnInfo,
    weftYarnInfo,
    warpColors = [],
    weftColors = [],
    maxPosition = 0,
    weaveFactorRowsByThreadType = { 1: [], 2: [] }
  } = params;

  const DISPLAY_COLS = 25;
  const ROWS_PER_PAGE = 10;
  const FIXED_ROWS = 5;
  const tarakNoStr = (reedNo && dgts) ? `${reedNo}*${dgts}` : (reedNo || '');
  const safeLogoUrl = sanitizeImageSrc(logoUrl);
  const safeTarihStr = escapeHtml(tarihStr || '');
  const safeWeaveCode = escapeHtml(weaveCode || '');
  const safeTarakNoStr = escapeHtml(tarakNoStr || '');
  const safeReedWidth = escapeHtml(reedWidth || '');
  const safeWarpDensity = escapeHtml(warpDensity || '');
  const safeTotalThreads = escapeHtml(totalThreads || '');
  const safeWeftDensity = escapeHtml(weftDensity || '');
  const safeWeaveType = escapeHtml(weaveType || '');
  const safeThreadName = escapeHtml(threadName || '');
  const safeDescription = escapeHtml(description || '');
  const safeWeavePhotoUrl = sanitizeImageSrc(weavePhoto);
  const safeFabricPhotoUrl = sanitizeImageSrc(fabricPhoto);

  // Helper to count unique color rows for a given color array
  const countColorRows = (colors) => {
    const colorGroups = {};
    colors.forEach((posData) => {
      if (posData && posData.colors) {
        posData.colors.forEach(color => {
          const key = `${color.yarnName}_${color.yarnCode}_${color.colorName}_${color.colorCode}`;
          if (!colorGroups[key]) colorGroups[key] = true;
        });
      }
    });
    return Object.keys(colorGroups).length;
  };

  const warpRowCount = countColorRows(warpColors);
  const weftRowCount = countColorRows(weftColors);
  const getMaxPositionByColors = (colors) => {
    return colors.reduce((max, posData) => {
      if (!posData || !Array.isArray(posData.colors) || posData.colors.length === 0) {
        return max;
      }
      const pos = posData?.position || 0;
      return pos > max ? pos : max;
    }, 0);
  };
  const warpMaxPosition = getMaxPositionByColors(warpColors);
  const weftMaxPosition = getMaxPositionByColors(weftColors);
  const totalColorRows = warpRowCount + weftRowCount;
  const hasRowOverflow = warpRowCount > ROWS_PER_PAGE || weftRowCount > ROWS_PER_PAGE;
  const hasColumnOverflow = warpMaxPosition > DISPLAY_COLS || weftMaxPosition > DISPLAY_COLS;
  const shouldUseCompactSinglePage = totalColorRows <= 8 && !hasColumnOverflow;
  const TOTAL_TABLE_COLS = DISPLAY_COLS + 7; // 4 info cols + position cols + 3 summary cols

  const renderHeaderColGroup = () => {
    const fixedCols = ['145px', '120px', '145px', '120px'];
    const positionCols = Array.from({ length: DISPLAY_COLS }, () => '<col style="width:20px;" />').join('');
    const summaryCols = ['50px', '55px', '70px'];

    return `<colgroup>
      ${fixedCols.map((width) => `<col style="width:${width};" />`).join('')}
      ${positionCols}
      ${summaryCols.map((width) => `<col style="width:${width};" />`).join('')}
    </colgroup>`;
  };

  const renderColorColGroup = () => {
    const fixedCols = ['80px', '200px', '80px', '60px'];
    const positionCols = Array.from({ length: DISPLAY_COLS }, () => '<col style="width:20px;" />').join('');
    const summaryCols = ['50px', '55px', '70px'];

    return `<colgroup>
      ${fixedCols.map((width) => `<col style="width:${width};" />`).join('')}
      ${positionCols}
      ${summaryCols.map((width) => `<col style="width:${width};" />`).join('')}
    </colgroup>`;
  };

  const formatReportNumber = (value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    if (Number.isInteger(num)) return String(num);
    return num.toFixed(2).replace(/\.?0+$/, '');
  };

  // Reusable header table markup - uses same total columns as color report table
  const renderHeader = () => {
    const descriptionColspan = DISPLAY_COLS;
    const brandColspan = TOTAL_TABLE_COLS - 4 - descriptionColspan;

    return `
    <table class="header-table">
      ${renderHeaderColGroup()}
      <tr>
        <td class="label-cell"><strong>TİP NO</strong></td>
        <td class="value-cell">${safeWeaveCode}</td>
        <td class="label-cell"><strong>TARAK NO</strong></td>
        <td class="value-cell">${safeTarakNoStr}</td>
        <td class="description-area" colspan="${descriptionColspan}" rowspan="4" align="left" valign="top" style="text-align:left;vertical-align:top;padding:4px 8px 0 8px;">
          <div class="description-title" style="text-align:left;line-height:1.1;"><strong>AÇIKLAMA:</strong></div>
          <div class="description-text">${safeDescription || '&nbsp;'}</div>
        </td>
        <td class="brand-area" colspan="${brandColspan}" rowspan="4">
          <img src="${safeLogoUrl}" alt="FABCO" /><br/>
          <div class="company-name">Tekstil San. Ve Tic.A.ş</div>
          <div class="report-main-title">ÇÖZGÜ VE ATKI RENK RAPORLARI</div>
        </td>
      </tr>
      <tr>
        <td class="label-cell"><strong>KOMPOZİSYON</strong></td>
        <td class="value-cell">${safeThreadName || safeTarihStr}</td>
        <td class="label-cell"><strong>TARAK ENİ</strong></td>
        <td class="value-cell">${safeReedWidth}</td>
      </tr>
      <tr>
        <td class="label-cell"><strong>ÇÖZGÜ SIKLIĞI</strong></td>
        <td class="value-cell">${safeWarpDensity}</td>
        <td class="label-cell"><strong>TOPLAM TEL ADEDİ</strong></td>
        <td class="value-cell">${safeTotalThreads}</td>
      </tr>
      <tr>
        <td class="label-cell"><strong>ATKI SIKLIĞI</strong></td>
        <td class="value-cell">${safeWeftDensity}</td>
        <td class="label-cell"><strong>ÖRGÜ</strong></td>
        <td class="value-cell">${safeWeaveType}</td>
      </tr>
    </table>`;
  };

  const renderPageMetaBar = (globalPageIndex) => {
    const pageNo = globalPageIndex + 1;
    const leftContent = globalPageIndex === 0
      ? '<div class="page-meta-spacer"></div>'
      : `<div class="tip-code-box"><span class="tip-label">TİP NO</span><span class="tip-value">${safeWeaveCode}</span></div>`;

    return `
    <div class="page-meta-bar">
      <div class="page-meta-left">${leftContent}</div>
      <div class="page-number-box">Sayfa ${pageNo}</div>
    </div>`;
  };

  const renderPhotoPage = (globalPageIndex) => {
    const needsBreak = globalPageIndex > 0;
    const wrapClass = needsBreak ? 'report-wrap report-page-break' : 'report-wrap';
    const gapHtml = needsBreak ? '<div class="report-gap"></div>' : '';
    const fabricPhotoContent = safeFabricPhotoUrl
      ? `<img class="report-photo-large" src="${safeFabricPhotoUrl}" alt="Fabric" />`
      : '';
    const armurePhotoContent = safeWeavePhotoUrl
      ? `<img class="report-photo-large" src="${safeWeavePhotoUrl}" alt="Armure" />`
      : '';

    return `
      ${gapHtml}
      <div class="${wrapClass}">
        ${renderPageMetaBar(globalPageIndex)}
        <div class="photo-page-section">
          <div class="photo-page-left">
            <div class="photo-page-frame">
              ${fabricPhotoContent}
            </div>
          </div>
          <div class="photo-page-right">
            <div class="photo-page-frame">
              ${armurePhotoContent}
            </div>
          </div>
        </div>
      </div>`;
  };


  const buildColorRows = (colors, reportMaxPosition = 0, factorRanges = []) => {
    const colorGroups = {};
    colors.forEach((posData) => {
      if (posData && posData.colors) {
        posData.colors.forEach(color => {
          const key = `${color.yarnName}_${color.yarnCode}_${color.colorName}_${color.colorCode} `;
          if (!colorGroups[key]) {
            colorGroups[key] = {
              yarnName: color.yarnName,
              yarnCode: color.yarnCode,
              colorName: color.colorName,
              colorCode: color.colorCode,
              rowSorting: color.rowSorting || Number.MAX_SAFE_INTEGER,
              weight: color.weight || '',
              positions: {}
            };
          }
          colorGroups[key].positions[posData.position] = color.threadCount;
          if (color.rowSorting && color.rowSorting < colorGroups[key].rowSorting) {
            colorGroups[key].rowSorting = color.rowSorting;
          }
        });
      }
    });

    const colorArray = Object.values(colorGroups);

    // Calculate totals and percentages
    colorArray.forEach(color => {
      let total = 0;
      for (let i = 1; i <= Math.max(reportMaxPosition, DISPLAY_COLS); i++) {
        const threadCount = Number(color.positions[i]) || 0;
        const factorRange = factorRanges.find(range => i >= range.start && i <= range.end);
        const factor = factorRange ? Number(factorRange.factor) : 1;
        total += threadCount * (Number.isFinite(factor) && factor > 0 ? factor : 1);
      }
      color.total = Number(total.toFixed(2));
    });

    const grandTotal = Number(colorArray.reduce((sum, color) => sum + color.total, 0).toFixed(2));
    colorArray.forEach(color => {
      color.percentage = grandTotal > 0 ? ((color.total / grandTotal) * 100).toFixed(1) : '0.0';
    });

    colorArray.sort((a, b) => {
      const rowSortingDiff = (a.rowSorting || Number.MAX_SAFE_INTEGER) -
        (b.rowSorting || Number.MAX_SAFE_INTEGER);
      if (rowSortingDiff !== 0) return rowSortingDiff;

      const aName = (a.colorName || '') + '-' + (a.yarnName || '');
      const bName = (b.colorName || '') + '-' + (b.yarnName || '');
      return aName.localeCompare(bName, 'tr');
    });

    return {
      rows: colorArray,
      grandTotal,
    };
  };

  const renderColorReport = ({
    displayRows = [],
    grandTotal = 0,
    title,
    yarnLabel,
    startPosition = 1,
    showTotalRow = true,
    factorRanges = [],
  }) => {
    const pagePositions = Array.from({ length: DISPLAY_COLS }, (_, idx) => startPosition + idx);
    const rowsToRender = [...displayRows];
    const getFactorBackground = (position) => {
      const factorRangeIndex = factorRanges.findIndex(
        range => position >= range.start && position <= range.end
      );
      if (factorRangeIndex === -1) return '';
      return factorRangeIndex % 2 === 0 ? '#fff59d' : '#c8e6c9';
    };

    const getFactorCellStyle = (
      position,
      hideColumnBorders = false,
      includeBackground = true,
      showBoundaryLines = false,
    ) => {
      const background = includeBackground ? getFactorBackground(position) : '';
      const styles = [];
      if (background) styles.push(`background:${background}`);

      const factorRange = factorRanges.find(
        range => position >= range.start && position <= range.end
      );

      if (hideColumnBorders) {
        styles.push('border-left:none !important');
        styles.push('border-right:none !important');

        if (factorRange && showBoundaryLines) {
          const shadowParts = [];
          if (position === factorRange.start) shadowParts.push('inset 2px 0 0 #000');
          if (position === factorRange.end) shadowParts.push('inset -2px 0 0 #000');
          if (shadowParts.length > 0) styles.push(`box-shadow:${shadowParts.join(',')}`);
        }
      } else if (factorRange && showBoundaryLines) {
        if (position === factorRange.start) styles.push('border-left:2px solid #000 !important');
        if (position === factorRange.end) styles.push('border-right:2px solid #000 !important');
      }

      return styles.length > 0 ? ` style="${styles.join(';')};"` : '';
    };

    return `
      <div class="color-report-section">
        <table class="color-report-table">
          ${renderColorColGroup()}
          <thead>
            <tr>
              <th colspan="${DISPLAY_COLS + 7}" class="color-report-title" style="text-align:left;font-size:16px;font-weight:700;padding:8px 12px;background:#f0f0f0;border:2px solid #000;">${title}</th>
            </tr>
          <tr class="yarn-label-row">
            <td colspan="${DISPLAY_COLS + 4}" style="border:1px solid #000;"></td>
            <td colspan="3" style="border:1px solid #000;text-align:center;font-weight:600;">${yarnLabel}</td>
          </tr>
          <tr>
            <th class="yarn-code" style="width:80px;max-width:80px;font-size:11px;white-space:normal;word-wrap:break-word;">İPLİK NUMARASI</th>
            <th class="yarn-name" style="width:200px;max-width:300px;font-size:12px;white-space:normal;word-wrap:break-word;">İPLİK ADI</th>
            <th class="color-name" style="width:80px;max-width:80px;font-size:11px;white-space:normal;word-wrap:break-word;">RENK ADI</th>
            <th class="color-code" style="width:60px;max-width:60px;font-size:11px;white-space:normal;word-wrap:break-word;">RENK KODU</th>
            ${pagePositions.map(position =>
          `<th class="position-col"${getFactorCellStyle(position)}>${position}</th>`
    ).join('')}
            <th class="total-col" style="width:50px;max-width:50px;white-space:normal;word-wrap:break-word;">Toplam</th>
            <th class="percentage-col" style="width:55px;max-width:55px;">%</th>
            <th class="weight-col" style="width:70px;max-width:70px;font-size:10px;white-space:normal;word-wrap:break-word;">İPLİK GRAMAJI</th>
          </tr>
        </thead>
        <tbody>
            ${rowsToRender.map(color => `
              ${(() => {
    const safe = (value) => {
      const normalized = value == null ? '' : String(value);
      return normalized ? escapeHtml(normalized) : '';
    };
    return `
              <tr>
                <td class="yarn-code" style="width:80px;max-width:80px;font-size:12px;">${safe(color.yarnCode)}</td>
                <td class="yarn-name" style="width:200px;max-width:300px;font-size:12px;white-space:normal;overflow-wrap:anywhere;word-break:break-word;vertical-align:top;">${safe(color.yarnName)}</td>
                <td class="color-name" style="width:80px;max-width:80px;font-size:12px;white-space:normal;overflow-wrap:anywhere;word-break:break-word;vertical-align:top;">${safe(color.colorName)}</td>
                <td class="color-code" style="width:60px;max-width:60px;font-size:12px;">${safe(color.colorCode)}</td>
                ${pagePositions.map(position =>
      `<td class="position-col"${getFactorCellStyle(position)}>${safe(color.positions[position])}</td>`
    ).join('')}
                <td class="total-col" style="width:50px;max-width:50px;">${safe(formatReportNumber(color.total))}</td>
                <td class="percentage-col" style="width:55px;max-width:55px;">${color.yarnName ? color.percentage + '%' : ''}</td>
                <td class="weight-col" style="width:70px;max-width:70px;font-size:12px;">${color.weight ? (Number(color.weight) / 10000).toFixed(2) : ''}</td>
              </tr>
            `;
  })()}
            `).join('')}
          ${showTotalRow ? `<tr>
            <td class="color-name" colspan="4" style="text-align:right;font-weight:700;">Toplam</td>
            ${pagePositions.map(position =>
          `<td class="position-col"${getFactorCellStyle(position, true, false, true)}></td>`
    ).join('')}
            <td class="total-col" style="width:50px;max-width:50px;"><strong>${formatReportNumber(grandTotal)}</strong></td>
            <td class="percentage-col" style="width:55px;max-width:55px;"><strong>100.0%</strong></td>
            <td class="weight-col" style="width:70px;max-width:70px;"></td>
          </tr>` : ''}
          ${factorRanges.length > 0 ? `<tr class="factor-row">
            <td class="color-name" colspan="4" style="text-align:right;font-weight:700;">Çarpan</td>
            ${(() => {
      const cells = [];
      let positionIndex = 0;

      while (positionIndex < pagePositions.length) {
        const position = pagePositions[positionIndex];
        const factorRangeIndex = factorRanges.findIndex(
          range => position >= range.start && position <= range.end
        );

        if (factorRangeIndex === -1) {
          cells.push('<td class="position-col"></td>');
          positionIndex += 1;
          continue;
        }

        const factorRange = factorRanges[factorRangeIndex];
        const visibleStart = Math.max(factorRange.start, startPosition);
        const visibleEnd = Math.min(factorRange.end, startPosition + DISPLAY_COLS - 1);
        const visibleSpan = Math.max(1, visibleEnd - visibleStart + 1);
        const background = factorRangeIndex % 2 === 0 ? '#fff59d' : '#c8e6c9';
        const label = `X${formatReportNumber(factorRange.factor)}`;

        if (position < visibleStart) {
          cells.push('<td class="position-col"></td>');
          positionIndex += 1;
          continue;
        }

        cells.push(
          `<td class="position-col factor-range-merged" colspan="${visibleSpan}" style="background:${background};text-align:center;font-weight:700;border-left:2px solid #000 !important;border-right:2px solid #000 !important;">${escapeHtml(label)}</td>`
        );
        positionIndex += visibleSpan;
      }

      return cells.join('');
    })()}
            <td class="total-col" style="width:50px;max-width:50px;"></td>
            <td class="percentage-col" style="width:55px;max-width:55px;"></td>
            <td class="weight-col" style="width:70px;max-width:70px;"></td>
          </tr>` : ''}
        </tbody>
      </table>
      </div>
    `;
  };

  const renderReportPages = (colors, title, yarnLabel, reportMaxPosition, factorRanges = [], startingGlobalPageIndex = 0) => {
    const { rows, grandTotal } = buildColorRows(colors, reportMaxPosition, factorRanges);
    const colPageCount = Math.max(1, Math.ceil(Math.max(reportMaxPosition, DISPLAY_COLS) / DISPLAY_COLS));
    const rowPageCount = Math.max(1, Math.ceil(Math.max(rows.length, 1) / ROWS_PER_PAGE));

    let pageCount = 0;
    let html = '';

    // Row pages first, then column overflow pages
    for (let colPageIndex = 0; colPageIndex < colPageCount; colPageIndex += 1) {
      for (let rowPageIndex = 0; rowPageIndex < rowPageCount; rowPageIndex += 1) {
        const rowStart = rowPageIndex * ROWS_PER_PAGE;
        const rowEnd = rowStart + ROWS_PER_PAGE;
        const displayRows = rows.slice(rowStart, rowEnd);
        const startPosition = colPageIndex * DISPLAY_COLS + 1;
        const globalPageIndex = startingGlobalPageIndex + pageCount;
        const needsBreak = globalPageIndex > 0;
        const wrapClass = needsBreak ? 'report-wrap report-page-break' : 'report-wrap';
        const showTotalRow = true;
        const gapHtml = needsBreak ? '<div class="report-gap"></div>' : '';
        const pageMetaHtml = renderPageMetaBar(globalPageIndex);
        const headerHtml = globalPageIndex === 0 ? renderHeader() : '';

        html += `
      ${gapHtml}
      <div class="${wrapClass}">
        ${pageMetaHtml}
        ${headerHtml}
        ${renderColorReport({
    displayRows,
    grandTotal,
    title,
    yarnLabel,
    startPosition,
    showTotalRow,
    factorRanges,
  })}
      </div>`;
        pageCount += 1;
      }
    }

    return { html, pageCount };
  };

  if ((hasColumnOverflow || hasRowOverflow) && !shouldUseCompactSinglePage) {
    const warpFactorRanges = weaveFactorRowsByThreadType?.[2] || [];
    const weftFactorRanges = weaveFactorRowsByThreadType?.[1] || [];
    const warpPages = renderReportPages(
      warpColors,
      'ÇÖZGÜ RENK RAPORU',
      'Çözgü İpliği',
      warpMaxPosition,
      warpFactorRanges,
      0
    );
    const weftPages = renderReportPages(
      weftColors,
      'ATKI RENK RAPORU',
      'Atkı İpliği',
      weftMaxPosition,
      weftFactorRanges,
      warpPages.pageCount
    );
    const totalReportPageCount = warpPages.pageCount + weftPages.pageCount;
    return `
    ${warpPages.html}
    ${weftPages.html}
    ${renderPhotoPage(totalReportPageCount)}`;
  }

  // Single page: both reports together
  const warpFactorRanges = weaveFactorRowsByThreadType?.[2] || [];
  const weftFactorRanges = weaveFactorRowsByThreadType?.[1] || [];
  const warpData = buildColorRows(warpColors, warpMaxPosition, warpFactorRanges);
  const weftData = buildColorRows(weftColors, weftMaxPosition, weftFactorRanges);

  return `
      <div class="report-wrap">
        ${renderPageMetaBar(0)}
        ${renderHeader()}
    ${renderColorReport({
    displayRows: warpData.rows,
    grandTotal: warpData.grandTotal,
    title: 'ÇÖZGÜ RENK RAPORU',
    yarnLabel: 'Çözgü İpliği',
    startPosition: 1,
    showTotalRow: true,
    factorRanges: warpFactorRanges,
  })}
    ${renderColorReport({
    displayRows: weftData.rows,
    grandTotal: weftData.grandTotal,
    title: 'ATKI RENK RAPORU',
    yarnLabel: 'Atkı İpliği',
    startPosition: 1,
    showTotalRow: true,
    factorRanges: weftFactorRanges,
  })}
  </div>
  ${renderPhotoPage(1)}`;
}

// Build a table-based HTML optimized for Excel rendering
function buildExcelHTMLFromCtx(ctx) {
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />
    <title>Çağlık Raporu</title>
    <style>
      ${templateCSS}
    </style>
  </head><body>
      ${renderExcelMarkup(ctx)}
    </body></html>`;
  return html;
}

function renderExcelMarkup(params) {
  return renderTemplateMarkup(params);
}
